import {
  Component,
  OnInit,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { Moment } from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { CustomDatepickerHeader } from './custom-datepicker-header';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FloatLabelType, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import * as moment from 'moment';

class CustomDateAdapter extends MomentDateAdapter {
  getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {
    const currentLang = this.locale || 'es';
    if (currentLang === 'es') {
      return ['Dom.', 'Lun.', 'Mar.', 'Mié.', 'Jue.', 'Vie.', 'Sáb.'];
    }
    return ['Sun.', 'Mon.', 'Tue.', 'Wed.', 'Thu.', 'Fri.', 'Sat.'];
  }

  createDate(year: number, month: number, date: number): Moment {
    // Crear la fecha en UTC
    return moment.utc([year, month, date]);
  }
}

@Component({
  selector: 'common-custom-datepickers',
  templateUrl: './custom-datepickers.component.html',
  styleUrls: ['./custom-datepickers.component.scss'],
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (translate: TranslateService) => translate.currentLang,
      deps: [TranslateService]
    },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY',
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
          weekdayLabel: 'ddd'
        },
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        floatLabel: 'never'
      },
    }
  ]
})
export class CustomDatepickersComponent implements OnInit, OnChanges {
  @ViewChild('picker') picker: MatDateRangePicker<Moment>;
  @Output() dateRangeChange = new EventEmitter<{ start: Moment | null, end: Moment | null }>();
  @Output() dateRangeClear = new EventEmitter<void>();

  @Input() defaultStartDate: Moment | null = null;
  @Input() defaultEndDate: Moment | null = null;

  customHeader = CustomDatepickerHeader;

  range = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  });

  @Input() labelStartDate = 'date.startDate';
  @Input() labelEndDate = 'date.endDate';
  @Input() labelCancel = 'modal.button.cancel';
  @Input() labelAccept = 'modal.button.accept';
  @Input() labelTitle = 'date.lastConnection';
  @Input() floatLabelType: FloatLabelType = 'never';

  constructor() { }

  ngOnInit(): void {
    this.setDefaultDates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['defaultStartDate'] || changes['defaultEndDate']) && !changes['defaultStartDate']?.firstChange) {
      this.setDefaultDates();
    }
  }

  private setDefaultDates(): void {
    if (this.defaultStartDate || this.defaultEndDate) {
      this.range.patchValue({
        start: this.defaultStartDate ? moment.utc(this.defaultStartDate) : null,
        end: this.defaultEndDate ? moment.utc(this.defaultEndDate) : null
      });
    }
  }

  get hasDateSelected(): boolean {
    return !!(this.range.get('start')?.value || this.range.get('end')?.value);
  }

  onEventSubmit() {
    const startDate = this.range.get('start')?.value;
    const endDate = this.range.get('end')?.value;

    this.dateRangeChange.emit({
      start: startDate ? moment.utc(startDate).startOf('day') : null,
      end: endDate ? moment.utc(endDate).endOf('day') : null
    });
  }

  clearDateRange(): void {
    this.range.reset();
    this.dateRangeClear.emit();
  }
}
